
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ChevronDownIcon, ChevronRightIcon, MessageSquareIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage } from '../types';
import { helpItems } from '../data/helpData';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

interface HelpPageProps {
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const HelpPage: React.FC<HelpPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();

    useSEO(
        t('helpPage_seo_title'),
        t('helpPage_seo_desc'),
        t('helpPage_seo_keywords').split(',').map(k => k.trim())
    );

    const [searchQuery, setSearchQuery] = useState('');
    const [openItems, setOpenItems] = useState<Set<string>>(new Set());

    const categories = ['getting-started', 'virtual-try-on', 'wardrobe', 'account', 'technical'];

    const toggleItem = (id: string) => {
        setOpenItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const filteredItems = useMemo(() => {
        if (!searchQuery) return helpItems;
        const lowerQuery = searchQuery.toLowerCase();
        return helpItems.filter(item =>
            t(item.questionKey).toLowerCase().includes(lowerQuery) ||
            t(item.answerKey).toLowerCase().includes(lowerQuery)
        );
    }, [searchQuery, t]);

    const renderCategory = (category: string) => {
        const items = filteredItems.filter(i => i.category === category);
        if (items.length === 0) return null;

        return (
            <div key={category} className="mb-8">
                <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 capitalize">
                    {t(`help_category_${category}`)}
                </h3>
                <div className="space-y-3">
                    {items.map(item => (
                        <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                            <button
                                onClick={() => toggleItem(item.id)}
                                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors focus:outline-none"
                            >
                                <span className="font-medium text-gray-800 dark:text-gray-200">{t(item.questionKey)}</span>
                                {openItems.has(item.id) ?
                                    <ChevronDownIcon className="w-5 h-5 text-indigo-500" /> :
                                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                                }
                            </button>
                            <AnimatePresence>
                                {openItems.has(item.id) && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="bg-gray-50 dark:bg-gray-900/30"
                                    >
                                        <p className="p-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-100 dark:border-gray-700">
                                            {t(item.answerKey)}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 overflow-y-auto custom-scrollbar"
        >
            {/* Header */}
            <div className="bg-indigo-600 dark:bg-indigo-900 text-white py-16 px-6 text-center">
                <h1 className="text-4xl font-serif font-bold mb-4">{t('helpPage_title')}</h1>
                <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">{t('helpPage_subtitle')}</p>

                <div className="relative max-w-lg mx-auto">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('helpPage_searchPlaceholder')}
                        className="w-full py-3 pl-12 pr-4 rounded-full text-gray-800 shadow-lg focus:ring-4 focus:ring-indigo-400 outline-none"
                    />
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow max-w-4xl w-full mx-auto p-6 md:p-12">
                {categories.map(cat => renderCategory(cat))}

                {filteredItems.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>{t('helpPage_noResults')}</p>
                    </div>
                )}

                {/* Chatbot CTA */}
                <div className="mt-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-white text-center shadow-xl">
                    <MessageSquareIcon className="w-12 h-12 mx-auto mb-4 text-white/90" />
                    <h3 className="text-2xl font-bold mb-2">{t('helpPage_chatbot_title')}</h3>
                    <p className="mb-6 text-white/90">{t('helpPage_chatbot_desc')}</p>
                    <button
                        onClick={() => onNavigate('styling-advisor')}
                        className="bg-white text-indigo-600 font-bold py-3 px-8 rounded-full hover:bg-gray-100 transition-colors shadow-md"
                    >
                        {t('helpPage_chatbot_button')}
                    </button>
                </div>
            </div>

            <Footer onNavigate={onNavigate} />
        </motion.div>
    );
};

export default HelpPage;
